// @ts-check
import api from './api'
import { prop } from 'ramda'
import { empty } from '@bluecrew/bluecrew-lib'
import { SplitFactory } from '@splitsoftware/splitio'
import { BEARER_TOKEN_NAME, JWT_TOKEN_NAME } from '../constants'
import CognitoUtils from './cognitoUtils';
import { getParameterByName } from './utils'

/**
 * NOTE: This is only a PARTIAL typing, other fields may exist on this object
 * @typedef UserDetails
 * @type {object}
 * @property {string} username A users' email 
 * @property {string} id external_id
*/

class User {
  can (permission) {
    return this.permissions.indexOf(permission) > -1
  }
}

class Auth {
  /* @ngInject */
  constructor ($http, $window, $cookies, $location, $q, $state, segment, PermRoleStore) {
    this.$http = $http
    this.$window = $window
    this.$cookies = $cookies
    this.$location = $location
    this.$q = $q
    this.$state = $state
    this.me = {}
    this.algoliaSearchKey = null
    this.cookieDomain = CONFIG.COOKIE_DOMAIN
    this.isLocal = CONFIG.IS_LOCAL
    this.segment = segment
    this.PermRoleStore = PermRoleStore
    this.splitClient = null
    this.v2TokenPromise = null;
    this.splitClientUserKey = 'anonymous'
  }
  
  getCookieOptions() {
    const cookieOptions = {};
    if (!this.isLocal) cookieOptions.domain = this.cookieDomain
    let d = new Date()
    d.setMonth(d.getMonth() + 2)
    cookieOptions.expires = d.toString();
    return cookieOptions;
  }

  getParameterByName(name, url) {
    const cookies = Auth._getCookies();
    const cookieToken = cookies['X-Cognito-Token'];

    // Validate if the URL is null, empty, or undefined
    if (!url) {
      return cookieToken;
    }

    name = name.replace(/[\[\]]/g, '\\$&');
    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    let results = regex.exec(url);

    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  getUserDetails = () => {
    this.getDetails().then(details => {
      if (details.highest_cm_app_version.split('.')[0] >= 7 && details.isWorker) {
        document.location.href = CONFIG.CM7_WEB_URL
      }
      const userInfo = {
        id: details.id,
        internal_id: details.internalId,
        type: details.accountType,
        status: details.status,
        points: details.pointsTotal,
        level: details.intercom.level,
        level_status: details.intercom.level_status,
        phone_number: details.phone,
        phone_verified: details.phoneVerified,
        username: details.username,
        is_eb_i9_verified: details.is_eb_i9_verified,
        name: details.intercom.name,
        company_id: details.companyId,
        companyName: details.companyName,
        region: details.intercom.region
      }
      this.setUser(userInfo);
    });
    this.getDetails().then(details => {
      if (details.highest_cm_app_version.split('.')[0] >= 7 && details.isWorker) {
        document.location.href = CONFIG.CM7_WEB_URL
      }
    });
  }

  login (username, password) {
    return this.$http({
      method: 'POST',
      url: `${api.getBase()}/noauth/user/login`,
      data: {
        username,
        password
      }
    })
    .then((results) => {
      let data = prop('data', results);
      this.setToken(data.authToken);
      this.setUser(data.userInfo);
      this.getDetails().then(details => {
        if (details.highest_cm_app_version.split('.')[0] >= 7 && details.isWorker) {
          document.location.href = CONFIG.CM7_WEB_URL
        }
      });
      this.getV2Token();
    })
  }

  /**
   * Retrieve Legacy Tokens
   * @returns {Promise}
   */
  retrieveLegacyTokens () {
    return this.$http({
      method: 'POST',
      url: `${api.getBaseV2()}/token/exchange`,
      data: { audience: '' }
    })
    .then((results) => {
      let tokens = prop('tokens', results.data);
      this.setToken(tokens.v1);
      this.setV2Token(tokens.v2); // Double check that we don't need the v2 token to call any legacy services, then delete this
      this.getUserDetails();
    })
  }

  /**
   * Helper method to parse cookies into an object
   * @returns {Object<string, string>} An object containing all cookies as key-value pairs
   */
  static _getCookies() {
    return document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.split('=').map(c => c.trim());
      acc[key] = value;
      return acc;
    }, {});
  }

  /**
   * @typedef UserLoggedInResult
   * @type {object}
   * @property {"COGNITO_AUTHD_CM" | "COGNITO_AUTHD_WP" | "NOT_AUTHD"} result
   * @property {string | null} token
   */

  /**
   * Given a set of arguments determine if user is CM or WP, and attempt to locate authentication for the user.
   * Return a result which describes if the user is WP or CM, and a token, or NOT_AUTHD if the user is not authenticated.
   * @param {string} url
   * @param {import('./cognitoUtils').CognitoSession} cognitoAuthn
   * @return {UserLoggedInResult}
   */
  static _checkIfCmOrWpUserIsLoggedIn(url, cognitoAuthn) {
    const NOT_AUTHD = { result: "NOT_AUTHD", token: null };

    // Retrieve cognitoToken from cookies
    const cookies = Auth._getCookies();
    const cognitoIdToken = cookies['X-Cognito-Token'] || getParameterByName("cognitoToken", url);
    if (cognitoIdToken) {
      return { result: "COGNITO_AUTHD_CM", token: cognitoIdToken };
    }

    // If neither of the query params were in the url,
    // then proceed as if user is WP user
    if (cognitoAuthn.idToken && cognitoAuthn.idToken !== "") {
      return { result: "COGNITO_AUTHD_WP", token: cognitoAuthn.idToken };
    }

    // For WP users, if cognito is enabled, it being not present means 
    // they are not logged in. 
    return NOT_AUTHD;
  }

  /**
   * Check if the current user is "logged in"
   * @returns {boolean}
   */
  loggedIn() {
    // Token might exist in the queryString parameter as 'cognitoToken': CM users use this method to access the earnings tab
    const { result, token } = Auth._checkIfCmOrWpUserIsLoggedIn(
      this.$window.location.href,
      CognitoUtils.getCognitoAuth(),
    );

    if (result === "COGNITO_AUTHD_CM") {
      CognitoUtils.setUserIsCrewMember(true);
      // "Enable" cognito migration, since we bypas the normal setup in app.js
      // with this method of authentication. This will cause web-angular to switch
      // what domains it calls for api-v1/v2. The new gateways understand cognito authn
      if (!CognitoUtils.setCognitoToken(token)) {
        return false;
      }
      return true;
    }

    return result === "COGNITO_AUTHD_WP";
  }

  /**
   * Check if the current user session is valid (is it expired?)
   * @returns {boolean}
   */
  sessionIsValid () {
    // TODO(WC-30) Remove split specific code
    if (!CognitoUtils.getUserIsCrewMember()) {
      return CognitoUtils.sessionIsValid();
    }

    // Legacy auth tokens don't expire 
    // Well, legacy bearer tokens don't... So
    return true;
  }

  /**
   * Check if the api-v1 bearer token exists in local storage, and retrieve it if it does
   * @returns {string | null}
   */
  getV1TokenIfExists () {
    return this.$cookies.get(BEARER_TOKEN_NAME);
  }

  /**
   * Returns bearer token, if it exists
   * @returns {string|null}
   */
  getToken () {
    // if a user is at the reset route, don't look for stored tokens
    const isReset = this.$location.path() === '/reset';
    let bearer;
    if(!isReset) {
      bearer = this.$cookies.get(BEARER_TOKEN_NAME);
      // if bearer isn't in local storage try to find it in url params
      if(!bearer || bearer === 'null' || bearer === 'undefined') {
        bearer = this.getParameterByName('authToken', this.$window.location.href)
        const tempAuthToken = this.getParameterByName('auth', this.$window.location.href)
        // if bearer not found in storage nor in params send user to login
        // fixes endless loop seen in https://bluecrewjobs.atlassian.net/browse/INPE-834
        if(!bearer && !tempAuthToken) {
          this.$state.go('app.login');
        }
        // set the token in storage before returning so that the auth interceptor can find the token before it starts making requests
        this.setToken(bearer)
      }
    }
    const tokens = {
      authToken: bearer,
    }
    if (!tokens.authToken) return // Empty return with no tokens

    return tokens.authToken
  }

  setToken (authToken) {
    this.$cookies.put(BEARER_TOKEN_NAME, authToken)
  }

  setV2Token (authToken) {
    this.$cookies.put(JWT_TOKEN_NAME, authToken)
  }

  setUser (user) {
    this.$window.localStorage['user'] = JSON.stringify(user)
  }

  initializePendo ({ userTraits }) {
    window.pendo.initialize({
      visitor: {
        ...userTraits,
        id: userTraits.external_id,
        companyId: userTraits.company_id,
      },
    })
  }

  newSession (id) {
    // Get user traits and initialize Pendo and Segment
    return this.$http({
      method: 'GET',
      url: `${api.getBase()}/users/${id}/session`
    })
    .then(prop('data'))
    .then((traits) => {
      let integrations = {
        Pendo: this.me.type === 0 ? false : true
      }
      
      // Initialize Pendo for non-CM accounts
      if (traits.type !== 'BLUECREW') {
        this.initializePendo({ userTraits: traits })
      }

      // Initialize Segment for non-CM accounts
      if (traits.type !== 'BLUECREW') {
        return this.segment.identify(
          this.me.id,
          traits,
          integrations
        )
      } else {
        return
      }
    })
  }

  getV2Token = () => {
    if(!this.v2TokenPromise) {
      this.v2TokenPromise = this.$http({
        method: 'POST',
        url: `${api.getBaseV2()}/token/exchange`,
        data: { audience: '' }
      })
      .then((results) => {
        let tokens = prop('tokens', results.data);
        this.setV2Token(tokens.v2); // Double check that we don't need the v2 token to call any legacy services, then delete this
      })   
    }
    return this.v2TokenPromise;
  }

  /**
   * Get user details
   * @param {*} preventCache 
   * @returns {UserDetails}
   */
  getDetails (preventCache) {
    // after initial worker signup phone # is not set yet, so we want to avoid caching at that time
    if (preventCache || empty(this.me) || (this.me.type === 0 && (!this.me.phone || !this.me.zipcode))) {
      return this.$http({
        method: 'GET',
        url: `${api.getBase()}/user/details`
      })
      .then(prop('data'))
      .then(this.setDetails.bind(this))
    } else {
      return Promise.resolve(this.me)
    }
  }

  splitInit () {
    // Return split client if already initialized, but only if it was initialized with a user id
    if (this.splitClient && this.splitClientUserKey !== 'anonymous') {
      return this.splitClient
    }

    // Setting split key to anonymous, if we don't have a user id yet.
    // This is necessary if we're init'ing split before we have one.
    // It _can_ really be anything, but 'anonymous' works.
    // If we stop needing to have split init'd for the login page, then we can remove all of thi
    this.splitClientUserKey = this.me.id ?? "anonymous"

    const SPLIT_CONFIG = String(CONFIG.SPOOF_LOCAL_SPLITS) === 'false' ?
      {
        core: {
          authorizationKey: CONFIG.SPLIT_KEY,
          key: this.splitClientUserKey,
          trafficType: 'user'
        }
      } : CONFIG.LOCAL_SPLIT_CONFIG;
    const factory = SplitFactory(SPLIT_CONFIG);

    return factory.client()
  }

  setDetails (userDetails) {
    if (!userDetails.isRoleSupported) {
      this.logout()
      this.$state.go('app.login')
      return
    }
    
    const user = new User()

    for (let prop in userDetails) {
      if (userDetails.hasOwnProperty(prop)) {
        user[prop] = userDetails[prop]
      }
    }    

    user.role = user.accountType
    this.me = user
    this.algoliaSearchKey = user.algoliaSearchKey

    this.newSession(user.internalId)

    this.splitClient = this.splitInit() 
    
    return this.me
  }

  logout() {
    this.PermRoleStore.clearStore();
    // Clearing local storage will clear cognito tokens, thus "logging" the user out
    this.$window.localStorage.clear();
    this.$window.sessionStorage.clear();

    // Remove all cookies, including X-Cognito-Token
    let cookies = this.$cookies.getAll();
    for (let cookie in cookies) {
      this.$cookies.remove(cookie, { path: '/', domain: CONFIG.COOKIE_DOMAIN });
      this.$cookies.remove(cookie, { path: '/' }); // Mainly needed so cookies are also wiped when running in localhost mode
    }

    // Explicitly remove the X-Cognito-Token cookie
    document.cookie = 'X-Cognito-Token=; path=/; domain=' + CONFIG.COOKIE_DOMAIN + '; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
    document.cookie = 'X-Cognito-Token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';

    this.splitClient?.destroy();
    this.splitClient = null;
    this.segment.reset();

    // Clear Pendo user data
    window.pendo?.clearSession();

    this.me = null;

    // Refresh the page. Easiest way to invalidate all caches for now.
    this.$window.location.href = '/app.html';
    this.$state.go('app.login');

    const angularInLocalMode = this.$window.location.href.includes("localhost");
    const reactInLocalMode = CONFIG.REACT_APP_BASE.includes("localhost");

    // Only redirect to web-react if:
    // - Running locally, cognito is on, and web-react is also running locally
    // - OR Running deployed, and cognito is on
    if ((angularInLocalMode && reactInLocalMode) || !angularInLocalMode) {
      this.$window.location.href = `${CONFIG.REACT_APP_BASE}`;
    }
  }

  workerOrClientCreate (data) {
    return this.$http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/noauth/user/create`,
      data: data
    })
    .then(prop('data'))
    .catch(prop('data'))
  }
}

export default Auth
